import { useState, useEffect, Dispatch, SetStateAction } from 'react';

/**
 * Custom hook for managing state with localStorage persistence and cross-tab synchronization
 *
 * Provides a useState-like interface that automatically persists state to localStorage
 * and synchronizes across browser tabs using the StorageEvent API.
 *
 * @template T - The type of the state value
 * @param key - The localStorage key to use for persistence
 * @param defaultValue - The default value if no stored value exists
 *
 * @returns A tuple [value, setValue] matching the useState API
 *
 * @example
 * // Store an array of strings
 * const [favorites, setFavorites] = useLocalStorageState<string[]>('favorites', []);
 *
 * // Add a favorite
 * setFavorites(prev => [...prev, 'new-id']);
 *
 * @example
 * // Store an object
 * interface UserPreferences {
 *   theme: 'light' | 'dark';
 *   fontSize: number;
 * }
 *
 * const [prefs, setPrefs] = useLocalStorageState<UserPreferences>('user-prefs', {
 *   theme: 'light',
 *   fontSize: 14
 * });
 *
 * // Update preferences
 * setPrefs(prev => ({ ...prev, theme: 'dark' }));
 *
 * @example
 * // Cross-tab synchronization
 * // Changes made in one tab will automatically sync to other tabs
 * const [counter, setCounter] = useLocalStorageState<number>('counter', 0);
 *
 * // In tab 1: setCounter(5)
 * // In tab 2: counter will automatically update to 5
 */
export function useLocalStorageState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  // Lazy initialization from localStorage
  const [state, setState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // ignore write errors
    }
  }, [key, state]);

  // Sync across tabs via StorageEvent
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try {
          setState(JSON.parse(e.newValue) as T);
        } catch {}
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  return [state, setState];
}
